import React from "react";
import { Link } from "react-router-dom";
import { Archive, ArchiveRestore, ExternalLink, HandHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ResponderRow } from "@/components/social/OtterAssistResponderModal";
import { needHref, postHref } from "@/lib/otterAssistRoutes";

// Otter Assist — one Assistance Received card (OAD-107 … OAD-110).
//
// The object is RESPONDER + the need they offered to help with. The responder body is the exact
// `ResponderRow` used by My Needs, so the private status control, Message, Connect and Follow/Fan
// (with the existing personal-profile targeting) are one implementation and one canonical row.

const STATUS_COPY = {
  active: "Open for help", help_received: "Help received", closed: "Closed", expired: "Expired",
  cancelled: "Cancelled",
};
const URGENCY_COPY = {
  immediate: "Immediate — today", asap: "ASAP — within 48 hours", soon: "Soon — within 7 days",
  within_days: "Within a set number of days", flexible: "Flexible — no firm deadline",
};

export function AssistanceReceivedCard({ item, view, onArchive }) {
  return (
    <article className="rounded-xl border border-border bg-card px-4 py-2"
      data-testid={`assistance-received-card-${item.action_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2 pt-2">
        <div className="min-w-0">
          <p className="truncate text-[12px] font-[620] text-foreground"
            data-testid={`assistance-received-need-${item.action_id}`}>
            {item.need_summary || "Your Community Need"}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            <span data-testid={`assistance-received-status-${item.action_id}`}>
              {STATUS_COPY[item.assist_status] || item.assist_status}
            </span>
            {" · "}{URGENCY_COPY[item.urgency] || item.urgency}
            {item.deadline_at ? ` · Deadline ${new Date(item.deadline_at).toLocaleDateString()}` : ""}
            {item.offer_state === "withdrawn" ? " · Offer withdrawn" : ""}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {/* OAD-113 — this control used to be labelled "View Need" and open Chatter. The two
              destinations are now separate and each label describes where it goes. */}
          <Button asChild type="button" size="sm" variant="outline" className="h-7 text-[11px]">
            <Link to={needHref(item.assist_id, "assistance-received")}
              data-testid={`assistance-received-view-need-${item.action_id}`}>
              <HandHeart className="mr-1.5 h-3 w-3" /> View Need
            </Link>
          </Button>
          {item.post_id && (
            <Button asChild type="button" size="sm" variant="outline" className="h-7 text-[11px]">
              <Link to={postHref(item.post_id, "assistance-received")}
                data-testid={`assistance-received-view-post-${item.action_id}`}>
                <ExternalLink className="mr-1.5 h-3 w-3" /> View Post
              </Link>
            </Button>
          )}
          <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px]"
            onClick={() => onArchive(view !== "archived")}
            data-testid={`assistance-received-archive-${item.action_id}`}>
            {view === "archived"
              ? (<><ArchiveRestore className="mr-1.5 h-3 w-3" /> Restore</>)
              : (<><Archive className="mr-1.5 h-3 w-3" /> Archive</>)}
          </Button>
        </div>
      </div>
      <ul className="divide-y divide-border">
        <ResponderRow assistId={item.assist_id} responder={item} />
      </ul>
    </article>
  );
}

export default AssistanceReceivedCard;
